import React, { useEffect, useRef } from 'react';
import { FieldGuide, Label, TextInputLarge } from '../../components/forms/input';
import * as Button from '../../components/button/button';
import * as Page from '../../components/content/page-header';
import { FieldValues, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { usePostMutation, usePutMutation } from '../../hooks/use-mutations';
import { Select2 } from '../../components/forms/select';
import { Pageable, usePagination } from '../../hooks/use-pagination';
import { Protocol } from '../settings/types/protocol';
import { Icons } from '../../components/icons/icons';
import { useQuery } from '../../hooks/use-query';
import { MapServer } from './types/map-server';
import tenantService from '../../services/tenant-service';
import { Checkbox2 } from '../../components/forms/checkbox';

export const MapServerEditor = (): JSX.Element => {
  const saveRef = useRef<HTMLButtonElement>();
  const { register, handleSubmit, setValue, formState: { errors } } = useForm();
  const navigate = useNavigate();
  const params = useParams();
  const id = params.id as string;
  const postMutation = usePostMutation({ path: '/map-servers' });
  const putMutation = usePutMutation({ path: `/map-servers/${id}` });
  const protocol = usePagination<Pageable<Protocol>>('/protocols', { size: 9999 });
  const mapServer = useQuery<MapServer>({ path: `/map-servers/${id}`, options: { enabled: id !== 'new' } });

  useEffect(() => {
    if (mapServer.data) {
      setUpdateValues();
    }
  }, [mapServer.isSuccess]);

  useEffect(() => {
    if (postMutation.isSuccess || putMutation.isSuccess) {
      navigate('/map-servers');
    }
  }, [postMutation.isSuccess, putMutation.isSuccess]);

  const onSubmit = (fields: FieldValues): void => {
    id === 'new' ? postMutation.mutate(fields) : putMutation.mutate(fields);
  };

  const setUpdateValues = (): void => {
    for (const key in mapServer.data) {
      if (key === 'id') {
        continue;
      }
      setValue(key, mapServer.data[key as keyof MapServer]);
    }
  };

  return <div>
    <Page.Header>
      <Page.Tenant />
      <Page.Title>{id === 'new' ? 'Add A' : 'Edit'} Map Server</Page.Title>
      <Page.SubTitle>Add an external Map Server to the system that is not available in the Gold Master. Map servers in the Gold Master are added and configured in the Digital Data Library.</Page.SubTitle>
    </Page.Header>
    <div className="row">
      <div className="col-9 bg-light left-col">
        <form id="static-translation-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4">
            <Label name="key">Map Server Id*</Label>
            <TextInputLarge {...register('slug', { required: 'You must provide a value for Map Server Id.' })} errors={errors} placeholder="Map Server Id" disabled={id !== 'new'} />
            <FieldGuide>Usually same as the map server name.</FieldGuide>
          </div>
          <div className="mb-4">
            <Label name="name">Name</Label>
            <TextInputLarge {...register('name', { required: 'You must provide a value for Name.' })} errors={errors} placeholder="Name" />
            <FieldGuide> Usually same as the map server name.</FieldGuide>
          </div>
          <div className="mb-4">
            <Label name="protocolId">Protocol Id</Label>
            <Select2 {...register('protocolId', { required: 'You must provide a value for Protocol Id.' })} errors={errors}>
              <option value="">Select Protocol</option>
              {protocol.data?.content?.map(protocol => {
                return <option key={protocol.id} value={protocol.id}>{protocol.name}</option>;
              })}
            </Select2>
            <FieldGuide>Protocol used for map server.</FieldGuide>
          </div>
          <div className="mb-4">
            <Label name="protocolVersion">Protocol Version</Label>
            <TextInputLarge {...register('protocolVersion', { required: 'You must provide a value for Protocol Version.' })} errors={errors} placeholder="Protocol version" />
            <FieldGuide>Protocol used for map server.</FieldGuide>
          </div>
          <div className="mb-4">
            <Label name="software">Software</Label>
            <Select2 {...register('software', { required: 'You must provide a value for Software.' })} errors={errors}>
              <option value="">Select Software</option>
              {softwareList?.map(software => {
                return <option key={software.key} value={software.key}>{software.value}</option>;
              })}
            </Select2>
            <FieldGuide>Software being run on the map server.</FieldGuide>
          </div>
          <div className="mb-4">
            <Label name="urlKey">Licence URL Key</Label>
            <TextInputLarge {...register('urlKey')} placeholder="Licence URL Key" />
            <FieldGuide>Key for authenticating to the map server.</FieldGuide>
          </div>
           <div className="mb-4">
            <Label name="url">URL</Label>
            <TextInputLarge {...register('url', { required: 'You must provide a value for URL.' })} errors={errors} placeholder="URL" />
           </div>
          {tenantService.isGoldMaster() && (
            <div className="mb-4">
              <Checkbox2 {...register('locked')} />
            </div>
          )}
        </form>
      </div>
      <div className="col-3 d-flex flex-column">
        <div className="px-5">
          <div className="mb-3">
            <Button.Primary form="static-translation-form" type="submit" ref={saveRef} disabled={postMutation.isLoading || putMutation.isLoading}>
              {id === 'new' ? <><Icons.Add />Add</>: 'Update'}
            </Button.Primary>
          </div>
          <div className="mb-3">
            <Button.Tertiary onClick={(): void => navigate('/map-servers')} disabled={postMutation.isLoading || putMutation.isLoading}>
              Cancel
            </Button.Tertiary>
          </div>
        </div>
      </div>
    </div>
  </div>;
};

const softwareList = [
  { key: 'ESRI_ARC_GIS_SERVER_REST', value: 'Esri Arc Gis Server Rest' },
  { key: 'ESRI_ARC_GIS_SERVER_SOAP', value: 'Esri Arc Gis Server Soap' },
  { key: 'ESRI_ARC_IMS', value: 'Esri Arc Ims' },
  { key: 'GEO_SERVER_GSR', value: 'Geo Server Gsr' },
  { key: 'KML', value: 'Kml' },
  { key: 'MN_MAPSERVER', value: 'Mn Mapserver' },
  { key: 'OTHER', value: 'Other' }
];
